package com.arena_web.ui.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

// ---------------------------------------------------------------------------
// Scaled-value helpers: multiply a design-pixel value by the global --scale
// ---------------------------------------------------------------------------

/** Returns a CSS `calc()` expression that scales [px] by the global `--scale` variable.
 *  `--scale` is a length (e.g. 1px at 1920×1080, 0.5px at 960×540),
 *  so we multiply a unitless number by it: `calc(48 * var(--scale))` → `48px` at full size. */
fun s(px: Number): String = "calc($px * var(--scale))"

fun Modifier.sWidth(px: Number): Modifier = styleModifier { property("width", s(px)) }
fun Modifier.sHeight(px: Number): Modifier = styleModifier { property("height", s(px)) }
fun Modifier.sSize(px: Number): Modifier = styleModifier { property("width", s(px)); property("height", s(px)) }
fun Modifier.sMinWidth(px: Number): Modifier = styleModifier { property("min-width", s(px)) }
fun Modifier.sMaxWidth(px: Number): Modifier = styleModifier { property("max-width", s(px)) }
fun Modifier.sMinHeight(px: Number): Modifier = styleModifier { property("min-height", s(px)) }
fun Modifier.sMaxHeight(px: Number): Modifier = styleModifier { property("max-height", s(px)) }
fun Modifier.sFontSize(px: Number): Modifier = styleModifier { property("font-size", s(px)) }
fun Modifier.sLineHeight(px: Number): Modifier = styleModifier { property("line-height", s(px)) }
fun Modifier.sLetterSpacing(px: Number): Modifier = styleModifier { property("letter-spacing", s(px)) }
fun Modifier.sBorderRadius(px: Number): Modifier = styleModifier { property("border-radius", s(px)) }
fun Modifier.sGap(px: Number): Modifier = styleModifier { property("gap", s(px)) }
fun Modifier.sPadding(all: Number): Modifier = styleModifier { property("padding", s(all)) }
fun Modifier.sPadding(v: Number, h: Number): Modifier = styleModifier { property("padding", "${s(v)} ${s(h)}") }
fun Modifier.sPaddingLeft(px: Number): Modifier = styleModifier { property("padding-left", s(px)) }
fun Modifier.sPaddingRight(px: Number): Modifier = styleModifier { property("padding-right", s(px)) }
fun Modifier.sPaddingTop(px: Number): Modifier = styleModifier { property("padding-top", s(px)) }
fun Modifier.sPaddingBottom(px: Number): Modifier = styleModifier { property("padding-bottom", s(px)) }
fun Modifier.sMargin(all: Number): Modifier = styleModifier { property("margin", s(all)) }
fun Modifier.sMarginLeft(px: Number): Modifier = styleModifier { property("margin-left", s(px)) }
fun Modifier.sMarginRight(px: Number): Modifier = styleModifier { property("margin-right", s(px)) }
fun Modifier.sMarginTop(px: Number): Modifier = styleModifier { property("margin-top", s(px)) }
fun Modifier.sMarginBottom(px: Number): Modifier = styleModifier { property("margin-bottom", s(px)) }
fun Modifier.sTop(px: Number): Modifier = styleModifier { property("top", s(px)) }
fun Modifier.sLeft(px: Number): Modifier = styleModifier { property("left", s(px)) }
fun Modifier.sRight(px: Number): Modifier = styleModifier { property("right", s(px)) }
fun Modifier.sBottom(px: Number): Modifier = styleModifier { property("bottom", s(px)) }

// ---------------------------------------------------------------------------
// Mobile-scale helpers — multiply a design-px value (at 390-px reference) by
// the global `--mobile-scale` variable. The variable is `min(1px, 100vw/390)`,
// so values stay at their original size on 390-px+ phones but shrink linearly
// on narrower devices. Use these inside any composable that lives behind an
// `isMobile()` gate; `s()`-scaled helpers are clamped to 0.4 there and would
// give the wrong result.
// ---------------------------------------------------------------------------

fun m(px: Number): String = "calc($px * var(--mobile-scale))"

fun Modifier.mWidth(px: Number): Modifier = styleModifier { property("width", m(px)) }
fun Modifier.mHeight(px: Number): Modifier = styleModifier { property("height", m(px)) }
fun Modifier.mSize(px: Number): Modifier = styleModifier { property("width", m(px)); property("height", m(px)) }
fun Modifier.mMinWidth(px: Number): Modifier = styleModifier { property("min-width", m(px)) }
fun Modifier.mMaxWidth(px: Number): Modifier = styleModifier { property("max-width", m(px)) }
fun Modifier.mFontSize(px: Number): Modifier = styleModifier { property("font-size", m(px)) }
fun Modifier.mLineHeight(px: Number): Modifier = styleModifier { property("line-height", m(px)) }
fun Modifier.mLetterSpacing(px: Number): Modifier = styleModifier { property("letter-spacing", m(px)) }
fun Modifier.mBorderRadius(px: Number): Modifier = styleModifier { property("border-radius", m(px)) }
fun Modifier.mGap(px: Number): Modifier = styleModifier { property("gap", m(px)) }
fun Modifier.mPadding(all: Number): Modifier = styleModifier { property("padding", m(all)) }
fun Modifier.mPadding(v: Number, h: Number): Modifier = styleModifier { property("padding", "${m(v)} ${m(h)}") }
fun Modifier.mPaddingLeft(px: Number): Modifier = styleModifier { property("padding-left", m(px)) }
fun Modifier.mPaddingRight(px: Number): Modifier = styleModifier { property("padding-right", m(px)) }
fun Modifier.mPaddingTop(px: Number): Modifier = styleModifier { property("padding-top", m(px)) }
fun Modifier.mPaddingBottom(px: Number): Modifier = styleModifier { property("padding-bottom", m(px)) }
fun Modifier.mMargin(all: Number): Modifier = styleModifier { property("margin", m(all)) }
fun Modifier.mTop(px: Number): Modifier = styleModifier { property("top", m(px)) }
fun Modifier.mLeft(px: Number): Modifier = styleModifier { property("left", m(px)) }
fun Modifier.mRight(px: Number): Modifier = styleModifier { property("right", m(px)) }
fun Modifier.mBottom(px: Number): Modifier = styleModifier { property("bottom", m(px)) }

object Responsive {

    @OptIn(ExperimentalWasmJsInterop::class)
    @Composable
    fun <T> responsive(
        mobile: T,
        tablet: T? = null,
        desktop: T? = null,
        wide: T? = null
    ): T {
        return ResponsiveValues(
            base = mobile,
            sm = mobile,
            md = tablet ?: mobile,
            lg = desktop ?: tablet ?: mobile,
            xl = wide ?: desktop ?: tablet ?: mobile,
            xxl = wide ?: desktop ?: tablet ?: mobile
        ).get()
    }

    fun clamp(min: Number, preferred: Double, max: Number): String {
        return "clamp(${min}px, ${preferred}vw, ${max}px)"
    }

    fun clampVmin(min: Number, preferred: Double, max: Number): String {
        return "clamp(${min}px, ${preferred}vmin, ${max}px)"
    }

    fun autoClamp(
        valueFor1920px: Number,
        minMultiplier: Double = 0.5,
        maxMultiplier: Double = 1.2
    ): String {
        val preferred = (valueFor1920px.toDouble() / 1920.0) * 100.0
        val min = valueFor1920px.toDouble() * minMultiplier
        val max = valueFor1920px.toDouble() * maxMultiplier
        return clamp(min.toInt(), preferred, max.toInt())
    }

    fun autoClampFlexible(
        valueFor1920px: Number,
        minMultiplier: Double = 0.5,
        maxMultiplier: Double = 1.2
    ): String {

        val preferred = (valueFor1920px.toDouble() / 1080.0) * 100.0
        val min = valueFor1920px.toDouble() * minMultiplier
        val max = valueFor1920px.toDouble() * maxMultiplier
        return clampVmin(min.toInt(), preferred, max.toInt())
    }

    fun autoClampBoth(
        valueFor1920px: Number,
        minMultiplier: Double = 0.5,
        maxMultiplier: Double = 1.2
    ): String {
        val min = valueFor1920px.toDouble() * minMultiplier
        val max = valueFor1920px.toDouble() * maxMultiplier

        val preferredVw = (valueFor1920px.toDouble() / 1920.0) * 100.0

        val preferredVh = (valueFor1920px.toDouble() / 1080.0) * 100.0

        return "clamp(${min.toInt()}px, min(${preferredVw}vw, ${preferredVh}vh), ${max.toInt()}px)"
    }
}

fun Modifier.responsiveWidth(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("width", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsiveHeight(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("height", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsivePadding(
    verticalValueFor1920px: Number,
    horizontalValueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("padding", "${Responsive.autoClampBoth(verticalValueFor1920px, minMultiplier, maxMultiplier)} ${Responsive.autoClampBoth(horizontalValueFor1920px, minMultiplier, maxMultiplier)}")
    }
}

fun Modifier.responsiveFontSize(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("font-size", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsiveBorderRadius(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("border-radius", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

fun Modifier.responsiveGap(
    valueFor1920px: Number,
    minMultiplier: Double = 0.5,
    maxMultiplier: Double = 1.2
): Modifier {
    return this.styleModifier {
        property("gap", Responsive.autoClampBoth(valueFor1920px, minMultiplier, maxMultiplier))
    }
}

@Composable
fun isMobile(): Boolean {
    var isMobile by remember {
        mutableStateOf(
            try {
                window.innerWidth < 600
            } catch (e: Throwable) {
                false
            }
        )
    }

    DisposableEffect(Unit) {
        val updateIsMobile: (dynamic) -> Unit = { _ ->
            try {
                val newValue = window.innerWidth < 600
                if (isMobile != newValue) {
                    isMobile = newValue
                }
            } catch (e: Throwable) {
            }
        }

        window.addEventListener("resize", updateIsMobile)

        onDispose {
            window.removeEventListener("resize", updateIsMobile)
        }
    }

    return isMobile
}

object ResponsivePadding {
    val Mobile = 16.px
    val Tablet = 24.px
    val Desktop = 32.px
    val Wide = 48.px
}

object ResponsiveFontSize {
    val MobileH1 = 32.px
    val TabletH1 = 40.px
    val DesktopH1 = 48.px

    val MobileH2 = 24.px
    val TabletH2 = 30.px
    val DesktopH2 = 36.px

    val MobileBody = 14.px
    val TabletBody = 16.px
    val DesktopBody = 16.px
}
